import { useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useToast } from "@/components/ui/toast/useToast";
import {
  isOnCallContract,
  maintenanceContractCustomerLabel,
  openMaintenanceContractDocument,
  type MaintenanceContract,
} from "@/lib/wartungen";
import {
  contractCategoryLabels,
  contractDaysUntilEnd,
  contractDetailHref,
  contractEditHref,
  contractIntervalLabel,
  contractLocationLabels,
  contractRenewalLabel,
  contractRuntimeLabel,
  contractStatusClass,
  contractStatusLabel,
  contractSubtitle,
  contractTitle,
  normalizeContractStatus,
} from "../lib/campaignView";

export interface ContractCardProps {
  contract: MaintenanceContract;
  /** Anzahl Wartungen (Kampagnen) im Vertrag – optional, wenn bekannt. */
  campaignCount?: number;
  onCreateMaintenance?: (contract: MaintenanceContract) => void;
  className?: string;
}

export function ContractStatusBadge({ status, className }: { status?: string | null; className?: string }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        contractStatusClass(status),
        className,
      )}
    >
      {contractStatusLabel(status)}
    </span>
  );
}

function MetaRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-1.5 text-sm text-label-secondary">
      <span className="mt-0.5 shrink-0 text-label-tertiary" aria-hidden>
        {icon}
      </span>
      <dt className="sr-only">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/**
 * Karte eines Wartungsvertrags. Die gesamte Kachel führt zu den Wartungen des
 * Vertrags; „Neue Wartung“, „Bearbeiten“ und der PDF-Link liegen als eigene
 * Aktionen darüber (Stretched-Link-Muster, keine verschachtelten Links).
 */
export function ContractCard({ contract, campaignCount, onCreateMaintenance, className }: ContractCardProps) {
  const toast = useToast();
  const [pdfBusy, setPdfBusy] = useState(false);
  const id = Number(contract.id);
  const title = contractTitle(contract);
  const subtitle = contractSubtitle(contract);
  const customer = maintenanceContractCustomerLabel(contract) || "Kunde unbekannt";
  const locations = contractLocationLabels(contract);
  const categories = contractCategoryLabels(contract);
  const renewal = contractRenewalLabel(contract);
  const daysUntilEnd = contractDaysUntilEnd(contract);
  const statusKey = normalizeContractStatus(contract.status);
  const stillRelevant = statusKey !== "ended" && statusKey !== "cancelled";
  const endsSoon =
    stillRelevant && daysUntilEnd != null && daysUntilEnd >= 0 && daysUntilEnd <= 90 && !contract.auto_renew_enabled;
  const expired = stillRelevant && daysUntilEnd != null && daysUntilEnd < 0;
  const detailHref = contractDetailHref(id);
  const editHref = contractEditHref(id);
  const onCall = isOnCallContract(contract);
  const document = onCall ? null : contract.document ?? null;

  return (
    <article
      className={clsx(
        "group relative flex h-full flex-col rounded-ios border border-separator bg-card p-4 shadow-ios transition",
        "hover:border-accent/50 hover:shadow-ios-md focus-within:border-accent/50",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-label group-hover:text-accent" title={title}>
            <Link
              to={detailHref}
              className="after:absolute after:inset-0 after:rounded-ios after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-accent"
              aria-label={`${title} – Wartungen ansehen`}
            >
              {title}
            </Link>
          </h3>
          <p className="mt-0.5 truncate text-xs text-label-tertiary">
            {[subtitle, typeof campaignCount === "number" ? `${campaignCount} Wartung${campaignCount === 1 ? "" : "en"}` : ""]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <ContractStatusBadge status={contract.status} className="shrink-0" />
      </div>

      <dl className="mt-3 space-y-1.5">
        <MetaRow icon={<UserIcon className="h-4 w-4" />} label="Kunde">
          <span className="block truncate text-label" title={customer}>
            {customer}
          </span>
        </MetaRow>
        <MetaRow icon={<BuildingOffice2Icon className="h-4 w-4" />} label="Orte">
          {locations.length === 0 ? (
            <span className="text-label-tertiary">Keine Orte hinterlegt</span>
          ) : locations.length <= 2 ? (
            <span className="line-clamp-2">{locations.join(" · ")}</span>
          ) : (
            <span title={locations.join("\n")}>
              {locations.slice(0, 2).join(" · ")}{" "}
              <span className="text-label-tertiary">+{locations.length - 2} weitere</span>
            </span>
          )}
        </MetaRow>
        {onCall ? null : (
          <MetaRow icon={<CalendarDaysIcon className="h-4 w-4" />} label="Laufzeit">
            <span className={clsx(expired && "text-status-red", endsSoon && "text-status-orange")}>
              {contractRuntimeLabel(contract)}
              {expired ? " · abgelaufen" : endsSoon ? ` · endet in ${daysUntilEnd} Tagen` : ""}
            </span>
          </MetaRow>
        )}
        <MetaRow icon={<ArrowPathIcon className="h-4 w-4" />} label="Rhythmus">
          <span className="block first-letter:uppercase">{contractIntervalLabel(contract)}</span>
          {!onCall && renewal ? <span className="block text-xs text-label-tertiary">{renewal}</span> : null}
        </MetaRow>
      </dl>

      <div className="mt-3 flex flex-wrap items-center gap-1">
        {categories.length ? (
          categories.map((cat) => (
            <span key={cat} className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label-secondary">
              {cat}
            </span>
          ))
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-status-orange">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden />
            Keine Wartungskategorie – ohne Kategorie kann keine Wartung angelegt werden.
          </span>
        )}
      </div>

      <div className="mt-auto pt-4">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-accent">
          Wartungen ansehen
          <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
        </span>

        <span className="mt-2 flex items-center gap-1 border-t border-separator pt-2">
          {document ? (
            <button
              type="button"
              className="relative z-10 inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label disabled:opacity-50"
              title={`Vertrag als PDF öffnen (${document.filename})`}
              disabled={pdfBusy}
              onClick={() => {
                setPdfBusy(true);
                void openMaintenanceContractDocument(id, { filename: document.filename })
                  .catch((err: unknown) => toast.error(err instanceof Error ? err.message : "PDF konnte nicht geöffnet werden."))
                  .finally(() => setPdfBusy(false));
              }}
            >
              <DocumentTextIcon className="h-4 w-4" aria-hidden />
              PDF
            </button>
          ) : null}
          {onCreateMaintenance ? (
            <button
              type="button"
              className="relative z-10 inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label disabled:cursor-not-allowed disabled:opacity-50"
              disabled={categories.length === 0}
              title={
                categories.length === 0
                  ? onCall
                    ? "Bitte zuerst eine Kategorie hinterlegen."
                    : "Bitte zuerst eine Kategorie im Vertrag hinterlegen."
                  : "Neue Wartung anlegen"
              }
              onClick={() => onCreateMaintenance(contract)}
            >
              <PlusIcon className="h-4 w-4" aria-hidden />
              Wartung
            </button>
          ) : null}
          <Link
            to={editHref}
            className="relative z-10 ml-auto inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label"
            title={onCall ? "Bearbeiten" : "Vertrag bearbeiten"}
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden />
            Bearbeiten
          </Link>
        </span>
      </div>
    </article>
  );
}
